<script>
export default {
	name: 'LoginMenu',
	inheritAttrs: false,
};
</script>

<script setup>
import OverlayDrawer from '~/components/OverlayDrawer.vue';
import MobileDrawerHeader from '~/components/AppHeader/MobileDrawerHeader.vue';
import useDisableScroll from '~composables/useDisableScroll.js';
import { onClickOutside, useEventBus } from '@vueuse/core';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import CTAButton from '~/aem-components/CTAButton.vue';
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import AppHyperlink from '~/components/AppHyperlink.vue';
import { useFireflyAuthStore } from '~/logic/auth/firefly-auth-store.js';
import { useIsRTL } from '~/logic/helpers/is-rtl.js';

const { isRTL } = useIsRTL();

const props = defineProps({
	isVisible: { type: Boolean, default: false },
	preLoginItems: { type: Array, default: () => [] },
	preLoginMenuLabel: { type: String, default: 'Log in/Sign up with' },
	myAccountLabel: { type: String, default: '' },
	myAccount: { type: Array, default: () => [] },
	logoutLabel: { type: String, default: 'Logout' },
	
	helloLabel: { type: String, default: 'Hello' },
	accountNumberLabel: { type: String, default: 'Account Number' },
	userIdLabel: { type: String, default: 'User ID' },
	hideLogoutButton: { type: Boolean, default: false },
	
	mobileLogoImgSrc: { type: String, default: null },
	mobileLogoImgAlt: { type: String, default: null },
	mobileLogoOpenInNewTab: { type: Boolean, default: false },

	componentIdAA: { type: String, default: '' },
});
const emit = defineEmits([
	'update:isVisible',
]);


const internalVisible = ref(props.isVisible);

watch(() => props.isVisible, (newValue) => {
	internalVisible.value = newValue;
});
watch(internalVisible, (newValue) => {
	emit('update:isVisible', newValue);
});

useDisableScroll(internalVisible);

const hideOverlay = () => {
	internalVisible.value = false;
};


const handleRootBlurredWithin = (event) => {
	internalVisible.value = false;
};


const rootEl = ref(null);

onClickOutside(rootEl, (event) => {
	internalVisible.value = false;
});


const {
	isLoggedIn,
	userInfo,
	logout,
} = useFireflyAuthStore();

const globalBookFlightBus = useEventBus('booking-widget:enrich-points-toggle-switch');

const handleLogout = (event) => {
	logout();
	globalBookFlightBus.emit('update-toggle-switch', 'logout');
	emit('update:isVisible', false);
};

const siteName = window.siteName;

</script>

<template>
<template v-if="isMobileViewport">
	<!-- Mobile -->
	<OverlayDrawer
		:isVisible="props.isVisible"
		class="text-white"
		:data-use-theme="siteName"
		@update:is-visible="$emit('update:isVisible', $event)"
	>
		<div class="overflow-y-auto max-h-screen">
			<MobileDrawerHeader
				class="sticky top-0"
				:logoImgSrc="props.mobileLogoImgSrc"
				:logoImgAlt="props.mobileLogoImgAlt"
				:logoOpenInNewTab="props.mobileLogoOpenInNewTab"
				@click:header-x="internalVisible = false"
			/>
			<div
				v-if="!isLoggedIn"
				class="px-6 pt-4 pb-6"
			>
				<div class="typo-category-title">{{ props.preLoginMenuLabel }}</div>
				<div
					v-for="(preLoginMobileItem, index) in props.preLoginItems"
					:key="`prelogin-mobile-${index}`"
				>
					<div class="flex mt-4 gap-y-4 flex-col">
						<AppHyperlink
							v-aa="[
								{
									clickName: preLoginMobileItem.loginOptionTitle,
									clickComponentType: 'URL',
									componentName: 'LoginMenu',
									componentID: props.componentIdAA,
								},
								{
									name: preLoginMobileItem.loginOptionTitle,
									type: preLoginMobileItem.loginOptionExternalLink ? 'exit' : 'other',
								},
							]"
							class="block border-2 border-neutral-grey-light rounded-t-xl rounded-bl-xl p-4 bg-neutral-grey-ultralight text-neutral-white-base"
							style="--a-tag-color-hover: var(--neutral-white-base);"
							:href="preLoginMobileItem.mobileLoginOptionURL"
							:target="preLoginMobileItem.openInNewTabLogin ? '_blank' : '_self'"
							:showExternalLinkIcon="false"
						>
							<h6 class="menu-item-title flex gap-3 pb-2">
								{{ preLoginMobileItem.loginOptionTitle }}
								<icon-far-arrow-up-right-from-square
									v-if="preLoginMobileItem.loginOptionExternalLink"
									class="fill-current"
								/>
							</h6>
							<p class="menu-item-description typo-body-text-2 basis-full">{{ preLoginMobileItem.loginOptionDescription }}</p>
						</AppHyperlink>
					</div>
				</div>
			</div>
			<div
				v-else
				class="pb-6"
			>
				<div class="login-grid px-8 pt-8 pb-6 items-center bg-secondary-firefly-orange-ultralight text-black">
					<div class="flex flex-col">
						<p class="text-sm font-light">{{ props.helloLabel }}</p>
						<p class="font-bold">{{ userInfo.username }}</p>
					</div>
					<div class="flex mt-4.5 gap-12">
						<div class="flex flex-col">
							<p class="text-sm font-light">{{ props.accountNumberLabel }}</p>
							<p class="font-bold">{{ userInfo.accountNumber }}</p>
						</div>
						<div class="flex flex-col">
							<p class="text-sm font-light">{{ props.userIdLabel }}</p>
							<p class="font-bold">{{ userInfo.userId }}</p>
						</div>
					</div>
				</div>
				<div class="mt-7 mb-6 px-6">
					<div class="mb-5 text-xs font-bold text-neutral-grey-extradark uppercase">{{ props.myAccountLabel }}</div>
					<div
						v-for="(item, index) in props.myAccount"
						:key="index"
						class="account-menu-list mb-6 flex items-center"
					>
						<AppHyperlink
							v-aa="[
								{
									clickName: item.ctaText,
									clickComponentType: 'URL',
									componentName: 'LoginMenu',
									componentID: props.componentIdAA,
								},
								{
									name: item.ctaText,
									type: 'other',
								},
							]"
							style="--a-tag-color-hover: var(--primary-blue-light);"
							class="flex items-center"
							:href="item.ctaUrl"
							:target="item.openInNewTab ? '_blank' : '_self'"
						>
							<span>{{ item.ctaText }}</span>
						</AppHyperlink>
					</div>
					<CTAButton
						v-if="!props.hideLogoutButton"
						v-aa="[
							{
								clickName: props.logoutLabel,
								clickComponentType: 'Button',
								componentName: 'LoginMenu',
								componentID: props.componentIdAA,
							},
							{
								name: props.logoutLabel,
								type: 'other',
							},
						]"
						class="mt-10 font-semibold"
						:ctaText="props.logoutLabel"
						styleType="secondary"
						sizeType="small"
						:alignmentMobile="isRTL ? 'right' : 'left'"
						@click="handleLogout"
					>
					</CTAButton>
				</div>
			</div>
		</div>
	</OverlayDrawer>
</template>

<template v-else>
	<!-- Desktop -->
	<Teleport to="body">
		<div class="generic-backdrop"></div>
	</Teleport>
	<!-- 'transform' class is for Safari fix -->
	<div
		v-if="isVisible"
		ref="rootEl"
		v-focus-within
		class="LoginMenu bg-white overflow-hidden transform"
		v-bind="$attrs"
		tabindex="-1"
		:data-use-theme="siteName"
		@keydown.esc.capture="hideOverlay"
		@blur-within="handleRootBlurredWithin"
	>
		<div class="login-dropdown-container">
			<div v-if="!isLoggedIn" class="before-login-container p-8">
				<div class="typo-category-title">{{ props.preLoginMenuLabel }}</div>
				<div
					v-for="(preLoginDesktopItem, index) in props.preLoginItems"
					:key="`prelogin-desktop-${index}`"
				>
					<div class="flex mt-4 gap-y-4 flex-col">
						<AppHyperlink
							v-aa="[
								{
									clickName: preLoginDesktopItem.loginOptionTitle,
									clickComponentType: 'URL',
									componentName: 'LoginMenu',
									componentID: props.componentIdAA,
								},
								{
									name: preLoginDesktopItem.loginOptionTitle,
									type: 'other',
								},
							]"
							class="block border-2 border-neutral-grey-light rounded-t-xl rounded-bl-xl p-4 bg-neutral-grey-ultralight"
							style="--a-tag-color-hover: var(--primary-blue-light);"
							:href="preLoginDesktopItem.desktopLoginOptionURL"
							:target="preLoginDesktopItem.openInNewTabLogin ? '_blank' : '_self'"
							:showExternalLinkIcon="false"
						>
							<h6 class="menu-item-title flex gap-3 pb-2">
								{{ preLoginDesktopItem.loginOptionTitle }}
								<icon-far-arrow-up-right-from-square
									v-if="preLoginDesktopItem.loginOptionExternalLink"
									class="fill-current"
								/>
							</h6>
							<p class="menu-item-description typo-body-text-2 basis-full">{{ preLoginDesktopItem.loginOptionDescription }}</p>
						</AppHyperlink>
					</div>
				</div>
			</div>
			<div v-else class="after-login-container">
				<div class="login-grid px-8 pt-8 pb-6 items-center bg-secondary-firefly-orange-ultralight border-b border-neutral-grey-light">
					<div class="flex flex-col">
						<p class="text-sm font-light">{{ props.helloLabel }}</p>
						<p class="font-bold">{{ userInfo.username }}</p>
					</div>
					<div class="flex mt-4.5 gap-6">
						<div class="flex flex-col">
							<p class="text-sm font-light">{{ props.accountNumberLabel }}</p>
							<p class="font-bold">{{ userInfo.accountNumber }}</p>
						</div>
						<div class="divider w-px bg-primary-firefly-orange-base"></div>
						<div class="flex flex-col">
							<p class="text-sm font-light">{{ props.userIdLabel }}</p>
							<p class="font-bold">{{ userInfo.userId }}</p>
						</div>
					</div>
				</div>
				<div class="px-8 py-6">
					<div class="mb-5 text-xs font-bold text-neutral-grey-extradark uppercase">{{ props.myAccountLabel }}</div>
					<div
						v-for="(item, index) in props.myAccount"
						:key="index"
						class="account-menu-list mb-4 flex items-center"
					>
						<AppHyperlink
							v-aa="[
								{
									clickName: item.ctaText,
									clickComponentType: 'URL',
									componentName: 'LoginMenu',
									componentID: props.componentIdAA,
								},
								{
									name: item.ctaText,
									type: 'other',
								},
							]"
							:href="item.ctaUrl"
							:target="item.openInNewTab ? '_blank' : '_self'"
						>
							<span>{{ item.ctaText }}</span>
						</AppHyperlink>
					</div>
					<CTAButton
						v-if="!props.hideLogoutButton"
						v-aa="[
							{
								clickName: props.logoutLabel,
								clickComponentType: 'Button',
								componentName: 'LoginMenu',
								componentID: props.componentIdAA,
							},
							{
								name: props.logoutLabel,
								type: 'other',
							},
						]"
						class="mt-6 font-semibold"
						:ctaText="props.logoutLabel"
						styleType="secondary"
						sizeType="extra-small"
						:alignmentDesktop="isRTL ? 'right' : 'left'"
						@click="handleLogout"
					>
					</CTAButton>
				</div>
			</div>
		</div>
	</div>
</template>


</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.LoginMenu {
	position: fixed;
	z-index: 7000;
	// 100% minus container width, remaining balance divide 2, coz left and right side.
	// Then, add 1rem (minus is left, but add because we are using 'right' property) to account for padding
	right: calc( ((100% - var(--containerWidth)) / 2) + 1rem );
	width: 400px;
	// after applying transform to header, this 'top' need to change
	/* top: calc( var(--mobileDlAppNotificationHeight) + var(--headerNotificationHeight) + var(--headerHeight) ); */
	top: var(--headerHeight);
	transition: opacity 0.3s ease;
	min-height: 500px;
	@apply shadow-type-a;
	
	html[dir="rtl"] & {
		left: calc( ((100% - var(--containerWidth)) / 2) + 1rem );
		right: revert;
	}
}

.login-avatar {
	@apply items-center justify-center self-start row-span-3 rounded-full w-14 h-14 font-bold text-xl;

	display: flex;
	margin-top: 5px;
}

.login-dropdown-container {
	overflow-y: auto;
	height: auto;
	max-height: calc(100vh - var(--headerHeight));
}

.login-grid {
	
}

.login-details {
	grid-column-start: 2;
}

.account-menu-list {
	:deep(.AppHyperlink) {
		font-weight: 600;
		--a-tag-color: var(--primary-firefly-orange-base);
		--a-tag-color-hover: var(--primary-firefly-orange-base);
		
		&:hover {
			text-decoration: underline;
		}
	}
}

.before-login-container :deep(.AppHyperlink) {
	
	&:hover {
		text-decoration: none;
		
		.menu-item-title {
			text-decoration: underline;
		}
	}
}
.menu-item-title {
	color: var(--primary-firefly-orange-base);
}
.menu-item-description {
	color: var(--text-color);
}
</style>
